/**
 * 한글 파일 이벤트(v2) → 제품(/chat AI 한글, /create-saenggibu) "이어서 편집" 연결 유틸.
 *
 * - 마지막 결과 저장/복원(localStorage, 24h): 가입 복귀·환영 모달에서 같은 문서로 이어가기 위해
 *   결과와 입력값(fields)을 함께 저장한다.
 * - resolveHwpDocContinue(): 유형별 목적지를 계산한다.
 *   exam/notice → /chat?category=document (방금 만든 HWPX 첨부 + 편집 예시 칩, payload v3)
 *   record      → /create-saenggibu (생성기가 이미 쓰는 location.state prefill 형식)
 * - 페이지와 환영 모달(ClassPhotoTrialWelcome)이 같은 함수를 써서 목적지가 어긋나지 않게 한다.
 */
import {
  HWP_DOC_RESULT_STORAGE_KEY,
  HWP_DOC_RESULT_TTL_MS,
  getHwpDocConfig,
  getHwpDocOptionLabel,
  getHwpRecordSaenggibuCategory,
  isHwpDocType,
} from '../config/hwpDocEventConfig';
import { getApiUrl } from './apiConfig';
import {
  buildChatHandoffUrl,
  createChatHandoffPayload,
  storeChatHandoff,
} from './chatHandoff';

const HWPX_MIME = 'application/vnd.hancom.hwpx';
const SOURCE_DOC_ID_PATTERN = /^hwpdoc_[0-9a-f]{32}$/;

/**
 * /chat 핸드오프 소비 측: 공개 raw-bytes 엔드포인트로 방금 만든 HWPX 를 받아 File 로 만든다.
 * 이 File 을 chat 의 기존 첨부 경로(validateAndProcessFiles → uploadChatFile)에 그대로 넣는다.
 */
export const fetchHwpDocAsFile = async ({ docId, filename, fetchImpl } = {}) => {
  if (!SOURCE_DOC_ID_PATTERN.test(String(docId || ''))) {
    throw new Error('invalid hwp doc id');
  }
  const doFetch = fetchImpl || ((...args) => fetch(...args));
  const response = await doFetch(
    getApiUrl(`/api/events/hwp-doc/${encodeURIComponent(docId)}/raw-bytes`),
    { method: 'GET' },
  );
  if (!response?.ok) throw new Error(`hwp doc raw-bytes ${response?.status || 'failed'}`);
  const blob = await response.blob();
  const base = String(filename || '').trim() || '한글 문서.hwpx';
  const safeName = /\.hwpx?$/i.test(base) ? base : `${base}.hwpx`;
  return new File([blob], safeName, { type: HWPX_MIME });
};

/** 환영 모달 "한글 파일 저장" → 결과 페이지가 저장을 실행하도록 알리는 window 이벤트 이름. */
export const HWP_DOC_DOWNLOAD_REQUEST_EVENT = 'whatquiz:hwp-doc-download-request';
export const HWP_DOC_CONTINUE_PARAM = 'continue';

const SAENGGIBU_PATH = '/create-saenggibu';
const SCHOOL_LEVEL_LABEL = { elementary: '초등', middle: '중등', high: '고등' };

/** 마지막 결과를 24시간 로컬 저장 → 가입 후 돌아오면 결과 카드 복원 + 이어가기 목적지 계산. */
export const saveHwpDocLastResult = (result, fields = null) => {
  if (!result?.doc_id) return false;
  try {
    localStorage.setItem(HWP_DOC_RESULT_STORAGE_KEY, JSON.stringify({
      doc_id: result.doc_id,
      type: result.type,
      result_kind: result.result_kind,
      title: result.title,
      filename: result.filename,
      raw_bytes_path: result.raw_bytes_path,
      download_path: result.download_path,
      share_path: result.share_path,
      markdown: result.markdown,
      plain_text: result.plain_text,
      sentences: result.sentences,
      fields: fields && typeof fields === 'object' ? fields : null,
      ts: Date.now(),
    }));
    return true;
  } catch (_) {
    return false; // 사파리 프라이빗 등 저장 실패는 무시
  }
};

/**
 * 저장된 마지막 결과. docId/type 을 주면 일치할 때만 돌려준다(가입 복귀 ?doc= 복원용).
 * 인자 없이 부르면 유효한 마지막 결과를 그대로 돌려준다(환영 모달용).
 */
export const readHwpDocLastResult = (docId = null, type = null) => {
  try {
    const raw = localStorage.getItem(HWP_DOC_RESULT_STORAGE_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    if (!saved?.doc_id || !isHwpDocType(saved.type)) return null;
    if (docId && saved.doc_id !== docId) return null;
    if (type && saved.type !== type) return null;
    if (!saved.ts || Date.now() - saved.ts > HWP_DOC_RESULT_TTL_MS) {
      localStorage.removeItem(HWP_DOC_RESULT_STORAGE_KEY);
      return null;
    }
    return saved;
  } catch (_) {
    return null;
  }
};

const describeFields = (type, fields) => getHwpDocConfig(type).fields
  .map((field) => {
    const value = fields?.[field.key];
    if (value === '' || value === null || value === undefined) return null;
    const label = field.kind === 'chips' ? getHwpDocOptionLabel(type, field.key, value) : String(value);
    return `- ${field.label}: ${label}`;
  })
  .filter(Boolean);

/**
 * chat 핸드오프 본문. HWPX 유형은 파일이 첨부되므로 입력 조건만 짧게 넘기고,
 * 텍스트 유형(record)은 생성된 문장 본문을 그대로 넘긴다(폴백 경로용).
 */
export const buildHwpDocHandoffContent = (type, title, fields, result) => {
  const config = getHwpDocConfig(type);
  const lines = describeFields(type, fields);
  if (result?.result_kind === 'text') {
    const body = String(result.plain_text || result.markdown || '').trim();
    return [
      `[${config.eventLabel || '한글 파일 이벤트'}에서 만든 ${config.navLabel} 문장] ${title || ''}`.trim(),
      ...lines,
      '',
      body,
      '',
      '위 문장 초안을 바탕으로 표현을 다듬고 필요한 부분을 보완해주세요.',
    ].join('\n');
  }
  return [
    `[한글 파일 이벤트에서 만든 ${config.navLabel}] ${title || ''}`.trim(),
    ...lines,
    '',
    '첨부한 한글 파일(.hwpx)이 방금 만든 문서예요. 요청한 부분만 수정해 다시 한글 파일로 만들어주세요.',
  ].join('\n');
};

const firstSentence = (result) => {
  const sentences = Array.isArray(result?.sentences) ? result.sentences : [];
  const first = sentences.find((item) => String(item?.text || '').trim());
  if (first) return String(first.text).trim();
  const plain = String(result?.plain_text || '').trim();
  const match = plain.match(/\[1안\]\s*([\s\S]*?)(?:\n\s*\n\[|$)/);
  return (match ? match[1] : plain).trim();
};

/**
 * 이벤트에서 고른 성취기준([{key?, code, text}]) → 생성기 성취기준 참조([{key, curriculum, code, text}]).
 * 생성기(feature/yeongu/achievement-standards 의 achievementStandardRestore.refsFromHistoryRecord)가
 * location.state.achievementStandardRefs / achievementStandardKeys 를 읽어 validate 후 칩으로 복원하므로 그 키 이름을 맞춘다.
 * key 가 없는 항목은 복원할 수 없어 참조에서 빼고 텍스트(프롬프트 줄)로만 남긴다.
 */
export const toSaenggibuAchievementStandardRefs = (standards) => (
  (Array.isArray(standards) ? standards : [])
    .filter((item) => item && String(item.key || '').trim() && String(item.code || '').trim())
    .map((item) => ({
      key: String(item.key).trim(),
      curriculum: '2022',
      code: String(item.code).trim(),
      text: String(item.text || '').trim() || null,
    }))
);

/** 교과 활동명/성취기준 칸에 붙는 `[코드] 원문` 줄(생성기 appendStandardLines 와 같은 형식, 코드 기준 중복 제거). */
const standardLines = (standards) => {
  const seen = new Set();
  return (Array.isArray(standards) ? standards : [])
    .filter((item) => item && String(item.code || '').trim())
    .filter((item) => {
      const code = String(item.code).trim();
      if (seen.has(code)) return false;
      seen.add(code);
      return true;
    })
    .map((item) => `${String(item.code).trim()} ${String(item.text || '').trim()}`.trim());
};

/**
 * 생기부 생성기(/create-saenggibu) location.state prefill.
 * SaenggibuGenerationForm 이 initialState.prompt 를 카테고리별 태그로 파싱하므로 그 형식을 그대로 맞춘다.
 * 교과세특에서 고른 성취기준은 (1) 활동명/성취기준 칸에 `[코드] 원문` 줄로, (2) achievementStandardRefs/Keys 로 함께 넘긴다.
 */
export const buildHwpDocSaenggibuPrefill = (fields, result) => {
  const area = fields?.area || 'subject';
  const level = fields?.school_level || 'middle';
  const schoolLevel = SCHOOL_LEVEL_LABEL[level] || '중등';
  const activity = String(fields?.subject_or_club || '').trim();
  const observations = String(fields?.observations || '').trim();
  const draft = firstSentence(result);
  const draftBlock = draft ? `\n\n[참고 초안]\n${draft}` : '';
  const category = getHwpRecordSaenggibuCategory(area, level);
  const base = { category, schoolLevel, emphasizePoints: [], source: 'hwp_doc_event' };

  const isSubject = category === '교과' || category === '교과_고등';
  const standards = isSubject ? fields?.achievement_standards : [];
  const lines = standardLines(standards);
  const refs = toSaenggibuAchievementStandardRefs(standards);
  const withStandards = (state) => (
    refs.length > 0
      ? { ...state, achievementStandardRefs: refs, achievementStandardKeys: refs.map((ref) => ref.key) }
      : state
  );
  const activityBlock = (suffix) => [activity ? `${activity} ${suffix}` : suffix, ...lines].join('\n');

  if (category === '교과_고등') {
    return withStandards({
      ...base,
      prompt: `[성취기준/평가 계획표]\n${activityBlock('교과 활동')}\n\n[학생 활동 내용]\n${observations}\n\n[학생 정보]\n${draft}`,
    });
  }
  if (category === '교과') {
    return withStandards({
      ...base,
      prompt: `[활동명/성취기준]\n${activityBlock('수업 활동')}\n\n[활동내용]\n${observations}${draftBlock}`,
    });
  }
  if (category === '동아리') {
    return {
      ...base,
      prompt: `[동아리 정보]\n${activity || '동아리'}\n\n[학생 활동 내용]\n${observations}${draftBlock}`,
    };
  }
  if (category === '진로') {
    return {
      ...base,
      prompt: `[진로 활동]\n${observations}\n\n[학생 정보]\n${draft}`,
    };
  }
  return {
    ...base,
    prompt: `${observations}${draftBlock}`,
  };
};

/**
 * 유형별 이어가기 목적지.
 * @returns {{ destination: 'chat'|'saenggibu', to: string, options?: object } | null}
 */
export const resolveHwpDocContinue = ({ type, result, fields }) => {
  if (!result?.doc_id || !isHwpDocType(type)) return null;
  const config = getHwpDocConfig(type);
  const continueCta = config.continueCta || {};

  // record 는 설정이 어긋어도 /chat 으로 가지 않는다. destination 트래킹은 항상 saenggibu.
  if (type === 'record' || continueCta.destination === 'saenggibu') {
    return {
      destination: 'saenggibu',
      to: SAENGGIBU_PATH,
      options: { state: buildHwpDocSaenggibuPrefill(fields, result) },
    };
  }

  const category = continueCta.chatCategory || 'document';
  const fallback = { destination: 'chat', to: `/chat?category=${category}` };
  try {
    const payload = createChatHandoffPayload({
      href: `/chat?category=${category}`,
      sourceContent: buildHwpDocHandoffContent(type, result.title, fields, result),
      sourceCategory: `hwp_doc_event_${type}`,
      sourcePurpose: config.navLabel,
      sourceDocId: result.doc_id,
      sourceDocTitle: result.title,
      sourceDocFilename: result.filename,
      editSuggestions: continueCta.editSuggestions,
    });
    if (!payload) return fallback;
    const handoffId = storeChatHandoff(payload);
    if (handoffId) return { destination: 'chat', to: buildChatHandoffUrl(category, handoffId) };
    return { destination: 'chat', to: `/chat?category=${category}`, options: { state: { chatHandoff: payload } } };
  } catch (_) {
    return fallback;
  }
};
